import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ConfiguracaoApp, PericiasService, VariavelConfig } from '../../core/services/pericias.service';
import { Profissional, ProfissionalPayload } from '../../core/models/pericia.model';
import { RotinasAnaliticasService, RotinaAnaliticaAdmin } from '../../core/services/rotinas-analiticas.service';


interface IndicadorEconomicoResumo {
  id: number;
  codigo: string;
  nome: string;
  fonte: string;
  codigo_fonte: string;
  tipo_valor: string;
  ativo: boolean;
  qtd_valores: number;
  primeira_competencia: string | null;
  ultima_competencia: string | null;
  ultima_importacao: string | null;
  ultimo_valor_percentual: number | null;
  ultimo_fator_mensal: number | null;
  status: string;
}


interface IndicadorEconomicoValor {
  codigo: string;
  nome: string;
  competencia: string | null;
  valor_percentual: number | null;
  fator_mensal: number | null;
  fonte_url: string | null;
  importado_em: string | null;
}

@Component({
  selector: 'app-administracao',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './administracao.component.html',
  styleUrl: './administracao.component.scss'
})
export class AdministracaoComponent implements OnInit {
  menuAberto = true;
  itemAtivo: 'profissionais' | 'variaveis' | 'rotinas' | 'indicadores-economicos' = 'profissionais';

  profissionais = signal<Profissional[]>([]);
  mensagem = signal('');
  erro = signal('');
  editandoId: number | null = null;

  tipos = ['Perito', 'Assistente Técnico', 'Equipe de Apoio', 'Consultor'];
  statusOpcoes = ['Ativo', 'Inativo'];

  form: ProfissionalPayload = this.profissionalVazio();

  configuracao: ConfiguracaoApp = {
    diretorio_retorno: '',
    observacoes: '',
    variaveis: []
  };

  variavelEditandoIndex: number | null = null;
  variavelForm: VariavelConfig = this.variavelVazia();

  rotinasAnaliticas = signal<RotinaAnaliticaAdmin[]>([]);
  rotinaEditandoId: number | null = null;
  rotinaForm: RotinaAnaliticaAdmin = this.rotinaVazia();

  indicadoresEconomicos = signal<IndicadorEconomicoResumo[]>([]);
  indicadoresCarregando = false;
  importacaoIndicadoresCarregando = false;
  abaIndicadores: 'resumo' | 'valores' | 'importacao' = 'resumo';
  importacaoLogLinhas: string[] = [];
  ultimaImportacaoOk: string | null = null;
  indicadorCodigoSelecionado = '';
  indicadorValores = signal<IndicadorEconomicoValor[]>([]);
  indicadorValoresCarregando = false;

  constructor(
    private readonly service: PericiasService,
    private readonly rotinasService: RotinasAnaliticasService,
    private readonly http: HttpClient,
    private readonly route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      const tela = params.get('tela') || 'peritos-assistentes';
      this.aplicarTelaDoMenu(tela);
    });

    this.carregar();
    this.carregarConfiguracoes();
    this.carregarRotinas();
    this.carregarIndicadoresEconomicos();
  }

  aplicarTelaDoMenu(tela: string): void {
    const mapa: Record<string, 'profissionais' | 'variaveis' | 'rotinas' | 'indicadores-economicos'> = {
      'peritos-assistentes': 'profissionais',
      'clientes-partes': 'profissionais',
      'equipe-padrao': 'profissionais',
      'configuracoes': 'variaveis',
      'variaveis': 'variaveis',
      'diretorios': 'variaveis',
      'parametros-honorarios': 'variaveis',
      'modelos-honorarios': 'variaveis',
      'modelos-laudo': 'variaveis',
      'modelos-checklist': 'variaveis',
      'auditoria': 'variaveis',
      'importacao-exportacao': 'variaveis',
      'indicadores-economicos': 'indicadores-economicos',
      'backup': 'variaveis',
      'rotinas': 'rotinas'
    };

    this.itemAtivo = mapa[tela] || 'profissionais';
    this.erro.set('');
    this.mensagem.set('');
  }

  alternarMenu(): void {
    this.menuAberto = !this.menuAberto;
  }

  selecionarItem(item: 'profissionais' | 'variaveis' | 'rotinas' | 'indicadores-economicos'): void {
    this.itemAtivo = item;
    this.erro.set('');
    this.mensagem.set('');
  }

  carregar(): void {
    this.service.listarProfissionais().subscribe({
      next: dados => this.profissionais.set(dados),
      error: () => this.erro.set('Não foi possível carregar os profissionais.')
    });
  }

  carregarConfiguracoes(): void {
    this.service.listarVariaveis().subscribe({
      next: variaveis => {
        this.configuracao = {
          diretorio_retorno: variaveis.find(v => v.chave === 'diretorio_retorno_planilha')?.valor || '',
          observacoes: '',
          variaveis: variaveis || []
        };
      },
      error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível carregar as variáveis.')
    });
  }

  salvarConfiguracoes(): void {
    this.mensagem.set('Use o botão Cadastrar variável ou Salvar variável.');
  }

  novaVariavel(): void {
    this.variavelEditandoIndex = null;
    this.variavelForm = this.variavelVazia();
  }

  editarVariavel(index: number): void {
    this.variavelEditandoIndex = index;
    this.variavelForm = { ...this.configuracao.variaveis[index] };
  }

  salvarVariavel(): void {
    this.erro.set('');
    this.mensagem.set('');

    if (!this.variavelForm.nome.trim() || !this.variavelForm.chave.trim()) {
      this.erro.set('Informe nome e chave da variável.');
      return;
    }

    const item = { ...this.variavelForm };
    const chamada = this.variavelEditandoIndex === null
      ? this.service.criarVariavel(item)
      : this.service.atualizarVariavel(this.configuracao.variaveis[this.variavelEditandoIndex].chave, item);

    chamada.subscribe({
      next: (variaveis) => {
        this.configuracao.variaveis = variaveis || [];
        this.configuracao.diretorio_retorno = this.configuracao.variaveis.find(v => v.chave === 'diretorio_retorno_planilha')?.valor || '';
        this.novaVariavel();
        this.mensagem.set('Variável salva.');
      },
      error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível salvar a variável em /api/variaveis/salvar.')
    });
  }

  excluirVariavel(index: number): void {
    const variavel = this.configuracao.variaveis[index];
    if (!confirm(`Excluir a variável ${variavel.nome}?`)) return;

    this.service.excluirVariavel(variavel.chave).subscribe({
      next: () => {
        this.mensagem.set('Variável excluída.');
        this.carregarConfiguracoes();
    this.carregarRotinas();
    this.carregarIndicadoresEconomicos();
      },
      error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível excluir a variável.')
    });
  }


  carregarIndicadoresEconomicos(): void {
    this.indicadoresCarregando = true;
    this.http.get<IndicadorEconomicoResumo[]>('/api/indicadores/resumo').subscribe({
      next: dados => {
        const lista = dados || [];
        this.indicadoresEconomicos.set(lista);
        this.indicadoresCarregando = false;
        if (!this.indicadorCodigoSelecionado && lista.length > 0) {
          this.indicadorCodigoSelecionado = lista[0].codigo;
          this.carregarValoresIndicador();
        }
      },
      error: (err) => {
        this.erro.set(err?.error?.detail || 'Não foi possível carregar indicadores econômicos.');
        this.indicadoresCarregando = false;
      }
    });
  }

  selecionarIndicador(codigo: string): void {
    this.indicadorCodigoSelecionado = codigo;
    this.carregarValoresIndicador();
  }




  quebrarLinhasLog(valor: any): string[] {
    const texto = String(valor || '').replaceAll(String.fromCharCode(13), '');
    return texto
      .split(String.fromCharCode(10))
      .map(linha => linha.trimEnd())
      .filter(Boolean);
  }

  importarIndicadoresBcb(): void {
    if (!confirm('Atualizar indicadores econômicos pelo BCB/SGS agora?')) return;

    this.abaIndicadores = 'importacao';
    this.importacaoIndicadoresCarregando = true;
    this.importacaoLogLinhas = ['Iniciando importação BCB/SGS...'];
    this.mensagem.set('');
    this.erro.set('');

    this.http.post<any>('/api/admin/indicadores/importar', {}).subscribe({
      next: (res) => {
        this.importacaoIndicadoresCarregando = false;
        this.mensagem.set(res?.mensagem || 'Indicadores importados com sucesso.');

        const linhas: string[] = [];
        if (res?.mensagem) linhas.push(res.mensagem);
        if (res?.stdout) linhas.push(...this.quebrarLinhasLog(res.stdout));
        if (res?.stderr) linhas.push(...this.quebrarLinhasLog(res.stderr));

        this.importacaoLogLinhas = linhas.length ? linhas : ['Importação finalizada com sucesso.'];
        this.ultimaImportacaoOk = new Date().toISOString();

        this.carregarIndicadoresEconomicos();
      },
      error: (err) => {
        this.importacaoIndicadoresCarregando = false;
        const detalhe = err?.error?.detail;

        const linhas: string[] = ['Erro ao importar indicadores BCB/SGS.'];

        if (typeof detalhe === 'string') {
          linhas.push(detalhe);
          this.erro.set(detalhe);
        } else if (detalhe?.mensagem) {
          linhas.push(detalhe.mensagem);
          if (detalhe.stdout) linhas.push(...this.quebrarLinhasLog(detalhe.stdout));
          if (detalhe.stderr) linhas.push(...this.quebrarLinhasLog(detalhe.stderr));
          this.erro.set(detalhe.mensagem);
        } else {
          this.erro.set('Não foi possível importar indicadores BCB/SGS.');
        }

        this.importacaoLogLinhas = linhas;
      }
    });
  }

  selecionarIndicadorResumo(codigo: string): void {
    this.indicadorCodigoSelecionado = codigo;
    this.abaIndicadores = 'valores';
    this.carregarValoresIndicador();
  }

  periodicidadeIndicador(i: IndicadorEconomicoResumo): string {
    if (!i.primeira_competencia || !i.ultima_competencia || !i.qtd_valores) return '-';

    const ini = new Date(i.primeira_competencia);
    const fim = new Date(i.ultima_competencia);

    if (Number.isNaN(ini.getTime()) || Number.isNaN(fim.getTime())) return '-';

    const meses =
      (fim.getUTCFullYear() - ini.getUTCFullYear()) * 12 +
      (fim.getUTCMonth() - ini.getUTCMonth()) + 1;

    if (meses <= 0) return '-';

    // Se tem muito mais registros do que meses, é série diária ou quase diária.
    if (i.qtd_valores > meses * 2) return 'Diário';

    return 'Mensal';
  }

  ultimaImportacaoResumo(): string {
    const datas = this.indicadoresEconomicos()
      .map(i => i.ultima_importacao)
      .filter((v): v is string => !!v)
      .sort();

    if (!datas.length) return '-';

    return this.formatarDataHoraIndicador(datas[datas.length - 1]);
  }

  formatarDataHoraIndicador(valor: string | null): string {
    if (!valor) return '-';
    const data = new Date(valor);
    if (Number.isNaN(data.getTime())) return valor;
    return data.toLocaleString('pt-BR');
  }

  carregarValoresIndicador(): void {
    const codigo = (this.indicadorCodigoSelecionado || '').trim();

    if (!codigo) {
      this.indicadorValores.set([]);
      return;
    }

    this.indicadorValoresCarregando = true;
    this.http.get<IndicadorEconomicoValor[]>(`/api/indicadores/${codigo}/valores?limite=120`).subscribe({
      next: dados => {
        this.indicadorValores.set(dados || []);
        this.indicadorValoresCarregando = false;
      },
      error: (err) => {
        this.erro.set(err?.error?.detail || 'Não foi possível carregar valores do indicador.');
        this.indicadorValoresCarregando = false;
      }
    });
  }

  formatarFatorIndicador(valor: number | null): string {
    if (valor === null || valor === undefined) return '-';
    return valor.toLocaleString('pt-BR', { minimumFractionDigits: 6, maximumFractionDigits: 12 });
  }

  formatarDataIndicador(valor: string | null): string {
    if (!valor) return '-';
    const data = new Date(valor);
    if (Number.isNaN(data.getTime())) return valor;
    return data.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  }

  formatarPercentualIndicador(valor: number | null): string {
    if (valor === null || valor === undefined) return '-';
    return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) + '%';
  }

  novo(): void {
    this.editandoId = null;
    this.form = this.profissionalVazio();
  }

  editar(p: Profissional): void {
    this.editandoId = p.id;
    this.form = {
      nome: p.nome,
      tipo: p.tipo,
      registro: p.registro,
      email: p.email,
      telefone: p.telefone,
      status: p.status,
      observacoes: p.observacoes
    };
  }

  salvar(): void {
    this.erro.set('');
    this.mensagem.set('');

    if (!this.form.nome.trim()) {
      this.erro.set('Informe o nome.');
      return;
    }

    if (this.editandoId) {
      this.service.atualizarProfissional(this.editandoId, this.form).subscribe({
        next: () => {
          this.mensagem.set('Profissional atualizado.');
          this.novo();
          this.carregar();
        },
        error: () => this.erro.set('Não foi possível atualizar.')
      });
      return;
    }

    this.service.criarProfissional(this.form).subscribe({
      next: () => {
        this.mensagem.set('Profissional cadastrado.');
        this.novo();
        this.carregar();
      },
      error: () => this.erro.set('Não foi possível cadastrar.')
    });
  }

  excluir(p: Profissional): void {
    if (!confirm(`Excluir ${p.nome}?`)) return;
    this.service.excluirProfissional(p.id).subscribe({
      next: () => {
        this.mensagem.set('Profissional excluído.');
        this.carregar();
      },
      error: () => this.erro.set('Não foi possível excluir. Verifique se ele está vinculado a alguma perícia.')
    });
  }


  carregarRotinas(): void {
    this.rotinasService.listar().subscribe({
      next: dados => this.rotinasAnaliticas.set(dados || []),
      error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível carregar as rotinas analíticas. Execute o SQL 061 e reinicie o backend.')
    });
  }

  novaRotina(): void {
    this.rotinaEditandoId = null;
    this.rotinaForm = this.rotinaVazia();
  }

  editarRotina(rotina: RotinaAnaliticaAdmin): void {
    this.rotinaEditandoId = rotina.id || null;
    this.rotinaForm = { ...rotina };
  }

  salvarRotina(): void {
    this.erro.set('');
    this.mensagem.set('');

    if (!this.rotinaForm.nome?.trim()) {
      this.erro.set('Informe o nome da rotina analítica.');
      return;
    }

    const chamada = this.rotinaEditandoId
      ? this.rotinasService.atualizar(this.rotinaEditandoId, this.rotinaForm)
      : this.rotinasService.criar(this.rotinaForm);

    chamada.subscribe({
      next: () => {
        this.mensagem.set('Rotina analítica salva.');
        this.novaRotina();
        this.carregarRotinas();
    this.carregarIndicadoresEconomicos();
      },
      error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível salvar a rotina analítica.')
    });
  }

  alternarRotina(rotina: RotinaAnaliticaAdmin): void {
    if (!rotina.id) return;

    const novoStatus = !(rotina.ativo !== false);

    this.rotinasService.ativar(rotina.id, novoStatus).subscribe({
      next: () => {
        this.mensagem.set(novoStatus ? 'Rotina ativada.' : 'Rotina inativada.');
        this.carregarRotinas();
    this.carregarIndicadoresEconomicos();
      },
      error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível alterar o status da rotina.')
    });
  }

  excluirRotina(rotina: RotinaAnaliticaAdmin): void {
    if (!rotina.id) return;
    if (!confirm(`Excluir a rotina ${rotina.nome}? Se ela já tiver execuções, o sistema vai impedir.`)) return;

    this.rotinasService.excluir(rotina.id).subscribe({
      next: () => {
        this.mensagem.set('Rotina excluída.');
        this.carregarRotinas();
    this.carregarIndicadoresEconomicos();
      },
      error: (err) => this.erro.set(err?.error?.detail || 'Não foi possível excluir. Se houver execuções, inative a rotina.')
    });
  }

  private profissionalVazio(): ProfissionalPayload {
    return {
      nome: '',
      tipo: 'Perito',
      registro: '',
      email: '',
      telefone: '',
      status: 'Ativo',
      observacoes: ''
    };
  }

  private variavelVazia(): VariavelConfig {
    return {
      nome: '',
      chave: '',
      valor: '',
      descricao: ''
    };
  }

  private rotinaVazia(): RotinaAnaliticaAdmin {
    return {
      nome: '',
      tipo: 'Geral',
      objetivo: '',
      entrada_esperada: '',
      procedimento: '',
      criterio_achado: '',
      saida_esperada: '',
      ativo: true
    };
  }
}
